import { useGtm } from '@gtm-support/vue-gtm';

/**
 * Composable for tracking events with Google Tag Manager
 * @returns {Object} GTM tracking methods
 */
export function useGtmTracker() {
  const gtm = useGtm();
  
  /**
   * Track a button click event
   * @param {string} type - The type of button clicked
   * @param {string} [label] - Optional label for the button
   */
  function trackButtonClick(type, label = '') {
    gtm.trackEvent({
      event: 'button_click',
      category: 'engagement',
      action: 'click',
      label: label || `${type}_button`,
      value: 1
    });
  }
  
  /**
   * Track a custom event
   * @param {Object} eventData - Custom event data
   */
  function trackCustomEvent(eventData = {}) {
    gtm.trackEvent({
      event: 'custom_event',
      category: 'user_action',
      action: 'interaction',
      ...eventData
    });
  }

  /**
   * Track shop return start button click
   * @param {string} shopName - Name of the shop
   * @param {string} location - Where the button was clicked (hero, address, howto, etc.)
   */
  function trackReturnStart(shopName, location = '') {
    gtm.trackEvent({
      event: 'return_start',
      category: 'conversion',
      action: 'start_return_process',
      label: `${shopName}_${location}`,
      shop_name: shopName,
      button_location: location,
      value: 1
    });
  }
  
  /**
   * Track a page view manually (if not using router automatic tracking)
   * @param {string} pageName - Name of the page
   * @param {Object} [additionalData] - Additional data to track
   */
  function trackPageView(pageName, additionalData = {}) {
    gtm.trackEvent({
      event: 'page_view',
      page_name: pageName,
      ...additionalData
    });
  }

  /**
   * Track GA4 e-commerce purchase event
   * @param {Object} purchaseData - Purchase event data
   * @param {string} purchaseData.transaction_id - Unique transaction ID
   * @param {number} purchaseData.value - Total value of the transaction
   * @param {string} purchaseData.currency - Currency code (e.g., 'RON', 'USD')
   * @param {number} [purchaseData.tax] - Tax amount
   * @param {number} [purchaseData.shipping] - Shipping amount
   * @param {string} [purchaseData.coupon] - Coupon code used
   * @param {Array} purchaseData.items - Array of items purchased
   * @param {Object} [purchaseData.user_data] - User information
   * @param {Object} [purchaseData.custom_parameters] - Additional tracking parameters
   */
  function trackPurchase(purchaseData) {
    // Ensure required fields are present
    if (!purchaseData.transaction_id || !purchaseData.value || !purchaseData.currency) {
      return;
    }

    // Format the purchase event data according to GA4 standards
    const eventData = {
      event: 'purchase',
      ecommerce: {
        transaction_id: purchaseData.transaction_id,
        value: Number(purchaseData.value).toFixed(2),
        currency: purchaseData.currency,
        items: purchaseData.items || []
      }
    };

    // Add optional fields if provided
    if (purchaseData.tax !== undefined) {
      eventData.ecommerce.tax = Number(purchaseData.tax).toFixed(2);
    }
    
    if (purchaseData.shipping !== undefined) {
      eventData.ecommerce.shipping = Number(purchaseData.shipping).toFixed(2);
    }
    
    if (purchaseData.coupon) {
      eventData.ecommerce.coupon = purchaseData.coupon;
    }

    // Add user data if provided
    if (purchaseData.user_data) {
      eventData.user_data = purchaseData.user_data;
    }

    // Add custom parameters for additional tracking
    if (purchaseData.payment_method) {
      eventData.payment_method = purchaseData.payment_method;
    }

    if (purchaseData.shipping_method) {
      eventData.shipping_method = purchaseData.shipping_method;
    }

    // Add enhanced custom parameters
    if (purchaseData.custom_parameters) {
      Object.assign(eventData, purchaseData.custom_parameters);
    }

    // Push to dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
    window.dataLayer.push(eventData);
  }

  /**
   * Track GA4 e-commerce begin_checkout event
   * @param {Object} checkoutData - Checkout event data
   * @param {string} checkoutData.currency - Currency code
   * @param {number} checkoutData.value - Total value
   * @param {Array} checkoutData.items - Array of items
   * @param {string} [checkoutData.coupon] - Coupon code
   * @param {Object} [checkoutData.custom_parameters] - Additional tracking parameters
   */
  function trackBeginCheckout(checkoutData) {
    const eventData = {
      event: 'begin_checkout',
      ecommerce: {
        currency: checkoutData.currency,
        value: Number(checkoutData.value).toFixed(2),
        items: checkoutData.items || []
      }
    };

    if (checkoutData.coupon) {
      eventData.ecommerce.coupon = checkoutData.coupon;
    }

    // Add custom parameters
    if (checkoutData.custom_parameters) {
      Object.assign(eventData, checkoutData.custom_parameters);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(eventData);
  }

  /**
   * Track GA4 e-commerce add_payment_info event
   * @param {Object} paymentData - Payment info event data
   * @param {string} paymentData.currency - Currency code
   * @param {number} paymentData.value - Total value
   * @param {string} paymentData.payment_type - Payment method type
   * @param {Array} paymentData.items - Array of items
   * @param {Object} [paymentData.custom_parameters] - Additional tracking parameters
   */
  function trackAddPaymentInfo(paymentData) {
    const eventData = {
      event: 'add_payment_info',
      ecommerce: {
        currency: paymentData.currency,
        value: Number(paymentData.value).toFixed(2),
        payment_type: paymentData.payment_type,
        items: paymentData.items || []
      }
    };

    // Add custom parameters
    if (paymentData.custom_parameters) {
      Object.assign(eventData, paymentData.custom_parameters);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(eventData);
  }

  /**
   * Track GA4 e-commerce add_shipping_info event
   * @param {Object} shippingData - Shipping info event data
   * @param {string} shippingData.currency - Currency code
   * @param {number} shippingData.value - Total value
   * @param {string} shippingData.shipping_tier - Shipping method
   * @param {Array} shippingData.items - Array of items
   * @param {Object} [shippingData.custom_parameters] - Additional tracking parameters
   */
  function trackAddShippingInfo(shippingData) {
    const eventData = {
      event: 'add_shipping_info',
      ecommerce: {
        currency: shippingData.currency,
        value: Number(shippingData.value).toFixed(2),
        shipping_tier: shippingData.shipping_tier,
        items: shippingData.items || []
      }
    };

    // Add custom parameters
    if (shippingData.custom_parameters) {
      Object.assign(eventData, shippingData.custom_parameters);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(eventData);
  }

  /**
   * Helper function to format items for GA4 e-commerce tracking
   * @param {Array} orderItems - Array of order items from the application
   * @param {Object} shippingOption - Shipping option details
   * @param {string} serviceType - Service type name
   * @returns {Array} Formatted items array for GA4
   */
  function formatItemsForGA4(orderItems, shippingOption = null, serviceType = '') {
    if (!Array.isArray(orderItems)) return [];

    return orderItems.map((item, index) => ({
      item_id: `shipping_service_${shippingOption?.carrier_id || 'unknown'}`,
      item_name: item.name || serviceType || 'Shipping Service',
      affiliation: 'eAWB.ro',
      coupon: item.coupon || '',
      currency: 'RON',
      discount: item.discount || 0,
      index: index,
      item_brand: 'eAWB',
      item_category: 'Shipping',
      item_category2: shippingOption?.carrier_name || 'Unknown Carrier',
      item_category3: item.description || 'Shipping Service',
      item_list_id: 'shipping_services',
      item_list_name: 'Available Shipping Services',
      item_variant: shippingOption?.serviceName || serviceType || 'Standard',
      location_id: '',
      price: Number(item.price || 0).toFixed(2),
      quantity: item.quantity || 1
    }));
  }

  /**
   * Helper function to create custom parameters for enhanced tracking
   * @param {Object} options - Options object containing user and order data
   * @param {boolean} options.isUserLoggedIn - Whether user is logged in
   * @param {Object} options.shippingOrder - Shipping order object
   * @param {string} options.selectedPaymentMethod - Selected payment method
   * @param {Object} [options.newAddress] - New address data if user is not logged in
   * @param {Array} [options.savedAddresses] - Saved addresses array
   * @param {number} [options.selectedAddress] - Selected address index
   * @returns {Object} Custom parameters object for GA4 tracking
   */
  function createCustomParameters(options = {}) {
    const customParams = {};
    
    try {
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('europarcel_user') || '{}');
      
      // User details
      if (options.isUserLoggedIn) {
        customParams.customer_id = userData.customer_id || userData.id || '';
        customParams.customer_email = userData.email || '';
        customParams.customer_phone = userData.phone || '';
        customParams.customer_name = userData.name || userData.first_name || userData.full_name || '';
        customParams.user_logged_in = 'yes';
      } else {
        // For guest users, use billing address data
        if (options.newAddress) {
          customParams.customer_email = options.newAddress.email || '';
          customParams.customer_phone = options.newAddress.phone || '';
          customParams.customer_name = options.newAddress.fullName || '';
        }
        customParams.user_logged_in = 'no';
        customParams.customer_id = 'guest';
      }
      
      // Product details
      if (options.shippingOrder) {
        customParams.carrier = options.shippingOrder.shippingOption?.carrier_name || 'Unknown';
        customParams.service_type = options.shippingOrder.shippingOption?.serviceName || 'Standard';
        
        // Product type - determine if it's shipment/parcel or wallet top-up
        if (options.shippingOrder.type === 'wallet_topup') {
          customParams.product_type = 'wallet_topup';
        } else if (options.shippingOrder.packageInfo?.type === 'box') {
          customParams.product_type = 'document'; // envelope/document
        } else {
          customParams.product_type = 'parcel'; // regular parcel
        }
        
        // Additional shipping details
        customParams.carrier_id = options.shippingOrder.shippingOption?.carrier_id || '';
        customParams.service_id = options.shippingOrder.shippingOption?.service_id || '';
      }
      
      // Order details - Payment method
      if (options.selectedPaymentMethod) {
        const paymentMethodMap = {
          'card': 'saved_card',
          'new_card': 'new_card', 
          'wallet': 'eawb_wallet',
          'apple_pay': 'apple_pay',
          'google_pay': 'google_pay'
        };
        customParams.payment_method = paymentMethodMap[options.selectedPaymentMethod] || options.selectedPaymentMethod;
      }
      
      // Address type (for shipping info events)
      if (options.selectedAddress !== undefined) {
        customParams.address_type = options.selectedAddress === -1 ? 'new_address' : 'saved_address';
      }
      
         } catch (error) {
       // Silently handle custom parameters creation errors
     }
    
    return customParams;
  }

  return {
    trackButtonClick,
    trackCustomEvent,
    trackPageView,
    trackReturnStart,
    trackPurchase,
    trackBeginCheckout,
    trackAddPaymentInfo,
    trackAddShippingInfo,
    formatItemsForGA4,
    createCustomParameters,
    gtm, // Expose the raw GTM instance for advanced use cases
  };
} 